import { createContext, useContext, useEffect, useState } from "react";
import { fetchGlossary } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer";
import { X } from "lucide-react";

const GlossaryContext = createContext({ map: {}, list: [], loaded: false });

export function GlossaryProvider({ children, source }) {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetchGlossary(undefined, source).then((d) => { setList(d || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, [source]);
  const map = {};
  list.forEach((g) => {
    const key = (g.transliteration || g.term || "").trim().toLowerCase();
    if (key) map[key] = g;
  });
  return <GlossaryContext.Provider value={{ map, list, loaded }}>{children}</GlossaryContext.Provider>;
}

export const useGlossary = () => useContext(GlossaryContext);

// Touch devices get a larger native-style bottom sheet; desktop keeps the popover.
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const on = () => setCoarse(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return coarse;
}

function TermBody({ g, meaning }) {
  return (
    <>
      <p className="font-serif-latin text-base text-ink">
        {g.transliteration || g.term}
        {g.devanagari ? <span className="ml-2 font-deva text-ink-2">{g.devanagari}</span> : null}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{meaning}</p>
    </>
  );
}

// Wrap any label/word. If it matches a glossary term, it becomes tappable and
// shows the Devanagari + meaning. Otherwise renders plain text.
export function GlossaryTerm({ children, term, className = "" }) {
  const { map } = useGlossary();
  const { lang } = useLang();
  const coarse = useCoarsePointer();
  const [open, setOpen] = useState(false);
  const raw = term ?? (typeof children === "string" ? children : "");
  const key = String(raw).trim().toLowerCase();
  const g = map[key];
  if (!g) return <span className={className}>{children}</span>;
  // Prefer the AI-translated meaning for the active site language, falling back
  // to the English source when a translation is not yet available.
  const meaning = (g.meaning_i18n || {})[lang] || g.meaning;

  const triggerCls = `cursor-help border-b border-dotted border-saffron/60 decoration-dotted underline-offset-4 transition-colors hover:text-saffron ${className}`;
  const testid = `glossary-term-${key.replace(/\s+/g, "-")}`;

  // Mobile / touch → bottom sheet
  if (coarse) {
    return (
      <>
        <button type="button" data-testid={testid} onClick={() => setOpen(true)} className={triggerCls}>
          {children}
        </button>
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="border-line bg-surface" data-testid="glossary-sheet">
            <div className="relative mx-auto w-full max-w-lg px-6 pb-10 pt-6 text-left">
              <DrawerClose asChild>
                <button aria-label="Close" data-testid="glossary-sheet-close" className="absolute right-4 top-4 rounded-full p-1.5 text-ink-3 transition-colors hover:text-saffron">
                  <X className="h-5 w-5" />
                </button>
              </DrawerClose>
              <p className="overline-label mb-3 text-gold">Glossary</p>
              <p className="font-serif-latin text-2xl text-ink">
                {g.transliteration || g.term}
              </p>
              {g.devanagari ? <p className="mt-1 font-deva text-2xl text-ink-2 deva-lh">{g.devanagari}</p> : null}
              <p className="mt-4 text-base leading-relaxed text-ink-2">{meaning}</p>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Desktop → popover
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={triggerCls}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 border-line bg-surface p-4 text-left" data-testid="glossary-popover">
        <TermBody g={g} meaning={meaning} />
      </PopoverContent>
    </Popover>
  );
}
